import {useEffect, useRef, useState} from "react";
import axios from "axios";
import debounce from "lodash.debounce";
import {useMapEvents, useMap} from "react-leaflet";
import L from "leaflet";
import parse from "wellknown";
import {setQueryParam, normalizeDifficultyFilterRange} from "../Utils/utils";

const MapEvents = (props) => {

    const [selectedMunicipality, setSelectedMunicipality] = useState(null);
    const [selectedProvince, setSelectedProvince] = useState(null);
    const POSTAL_CODE_REGEX = /\b(\d{4})\b/;

    const {
        fetchMapElements,
        filters,
        getZoomLevel,
        isComboRoute,
        onRequestDone,
        onRequestError,
        onRequestStart
    } = props

    const nonLocationFilters = JSON.stringify(
        Object.fromEntries(
            Object.entries(filters).filter(([key]) => key !== 'municipality' && key !== 'province')
        )
    );

    //const DefaultLink = "https://localhost:44360/Umbraco/Api/RouteTest/Get";
    const DefaultLink = "/umbraco/api/routeTest/get";

    const searchBody = {
        method: "searchAdvanced",
        params: [
            {
                bounds: {
                    min: {
                        lon: 3.0157470703125004,
                        lat: 50.369992582877195,
                    },
                    max: {
                        lon: 5.960083007812501,
                        lat: 51.614605707797494,
                    },
                },
                "permission.readable": true,
                "bounds.comparator": "geometry",
                language: "nl",
                "group.id": [1933],
                "owner.id": [30581],
                "exclude.group.id": [55551],
            },
            "score DESC",
            100, //limit
            0, //offset
            {
                media: true,
                clusters: false,
            },
        ],
    };

    const mMap = useMap();
    const requestVersionRef = useRef(0);
    const debouncedFetchRoutes = useRef(debounce((run) => run(), 500)).current;

    useEffect(() => () => debouncedFetchRoutes.cancel(), []);

    const [routesData, setRoutesData] = useState([]);
    const [mapElements, setMapElements] = useState({});
    const [combinedRoutesData, setCombinedRoutesData] = useState({
        requestVersion: 0,
        routes: [],
    });
    const [center, setCenter] = useState();
    const [isMapReady, setIsMapReady] = useState(false);

    useEffect(() => {
        mMap.whenReady(() => setIsMapReady(true));
    }, []);

    // Bounds are only meaningful once Leaflet reports the map as ready, so the initial fetch waits
    // for that instead of running a second one next to it.
    useEffect(() => {
        if (!isMapReady) {
            return;
        }

        fetchRoutes();
    }, [isMapReady, nonLocationFilters, isComboRoute]);

    useEffect(() => {
        if (filters.municipality &&
            (!selectedMunicipality || selectedMunicipality.postalCode !== filters.municipality.postalCode)) {
            setSelectedMunicipality(filters.municipality);
        } else if (!filters.municipality && selectedMunicipality) {
            setSelectedMunicipality(null);
        }
    }, [filters.municipality]);

    useEffect(() => {
        if (filters.province &&
            (!selectedProvince || selectedProvince.name !== filters.province.name)) {
            setSelectedProvince(filters.province);
        } else if (!filters.province && selectedProvince) {
            setSelectedProvince(null);
        }
    }, [filters.province]);

    useEffect(() => {
        if (combinedRoutesData.requestVersion !== requestVersionRef.current || combinedRoutesData.routes.length !== 2) {
            return;
        }

            const allRoutes = [
                ...combinedRoutesData.routes[0].routes,
                ...combinedRoutesData.routes[1].routes,
            ];

            setRoutesData({
                routes: allRoutes,
                total: allRoutes.length,
            });
    }, [combinedRoutesData]);


    useEffect(() => {
        async function fetchData() {
            const requestVersion = requestVersionRef.current;
            var routeData = []

            if (routesData.routes !== undefined) {
                let allRouteids = []

                routesData.routes.forEach(route => {
                    allRouteids.push(route.id)
                })

                onRequestStart && onRequestStart();

                try {
                    const response = await fetchRouteDetails(allRouteids);
                    for (const route of routesData.routes) {
                        if (route.type.id === 10) continue;
                        const score = response.data[route.id]?.statistics?.survey?.generalAssessment?.average

                        if (selectedMunicipality || selectedProvince) {
                            const routeDetails = response.data[route.id];

                            if (selectedProvince &&
                                !isRouteInProvince(routeDetails, selectedProvince.postalCodeRange)) {
                                continue;
                            }
                        }

                        //Convert start point coordinates
                        var geoJsonObject = L.geoJson(parse(route.begin.wkt));
                        var layer = geoJsonObject.getLayers();
                        var coordinates = layer[0].feature.geometry.coordinates;

                        //Check route image
                        var image = "/images/placeholder.png";
                        if (route?.media?.length > 0) {
                            route.media.some(media => {
                                if (!(media.source?.name?.nl && media.source.name.nl === "YouTube")) {
                                    image = media?.uri?.nl;
                                    return true;
                                }
                                return false;
                            });
                        }

                        //Check route color
                        var color = "#ff0000";
                        if (route.configuration !== null) {
                            if (route.configuration.line !== undefined) {
                                color = route.configuration.line.normal.standard.color;
                            }
                        }
                        routeData.push({
                            id: route.id,
                            source: markerImageSelector(route.type.id),
                            position: [coordinates[1], coordinates[0]],
                            name: route.name.nl,
                            type: route.type.id,
                            distance: route.length / 1000,
                            imageUrl: image,
                            routeColor: color,
                            routing: response.data[route.id].geometry.google,
                            elevation: response.data[route.id].ascent?.total
                                ? response.data[route.id].ascent?.total
                                : response.data[route.id].descent?.total,
                            score: response.data[route.id].score,
                            reviewScore: score,
                            difficulty: response.data[route.id].difficulty,
                            difficultyAuthor: response.data[route.id].difficultyAuthor,
                        });
                    }
                } catch (error) {
                    console.error("Error fetching route details:", error);
                } finally {
                    onRequestDone && onRequestDone();
                }

                if (requestVersion !== requestVersionRef.current) {
                    return;
                }

                if (routeData.length > 0) routeData = sortRoutes(routeData);

                setMapElements({
                    data: {
                        clusters: [],
                        routes: routeData,
                    },
                });
            }
        }

        fetchData();
    }, [routesData]);

    useEffect(() => {
        fetchMapElements(mapElements);
    }, [mapElements]);

    const calculateDistance = (coord1, coord2) => {
        const {lat: lat1, lng: lon1} = coord1;
        const {lat: lat2, lng: lon2} = coord2;
        const earthRadius = 6371; // Earth's radius in kilometers
        const latDiff = degToRad(lat2 - lat1);
        const lonDiff = degToRad(lon2 - lon1);
        const a =
            Math.sin(latDiff / 2) * Math.sin(latDiff / 2) +
            Math.cos(degToRad(lat1)) *
            Math.cos(degToRad(lat2)) *
            Math.sin(lonDiff / 2) *
            Math.sin(lonDiff / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = earthRadius * c;
        return distance;
    };

    const degToRad = (deg) => {
        return deg * (Math.PI / 180);
    };

    const sortRoutes = (routes) => {
        const sortedRoutes = [...routes].sort(
            (item1, item2) =>
                calculateDistance(
                    L.latLng(item1.position[0], item1.position[1]),
                    center
                ) -
                calculateDistance(
                    L.latLng(item2.position[0], item2.position[1]),
                    center
                )
        );
        return sortedRoutes;
    };

    const markerImageSelector = (typeId) => {
        let image = "";
        //let baseUrl ="/assets/images/markers"
        let baseUrl = "/images/markers";
        switch (typeId) {
            case 9:
            case 25:
            case 26:
            case 49:
                image = `${baseUrl}/runMarker`;
                break;

            case 17:
            case 35:
            case 36:
                image = `${baseUrl}/horsebackMarker`;
                break;

            case 7:
                image = `${baseUrl}/mountainbikeMarker`;
                break;

            case 47:
                image = `${baseUrl}/gravelbikeMarker`;
                break;

            case 16:
            case 32:
                image = `${baseUrl}/kayakMarker`;
                break;

            case 19:
                image = `${baseUrl}/skateMarker`;
                break;

            case 2:
            case 8:
                image = `${baseUrl}/multiMoveMarker`;
                break;
        }
        return image;
    };

    const calculateCenter = (bounds) => {
        const latCenter = (bounds._northEast.lat + bounds._southWest.lat) / 2;
        const longCenter = (bounds._northEast.lng + bounds._southWest.lng) / 2;

        setCenter(L.latLng(latCenter, longCenter));
        return L.latLng(latCenter, longCenter);
    };

    // Kept outside the debounce: the zoom level drives marker sizes and the polyline threshold, and
    // the query string is read back when returning from a route detail page.
    const syncViewport = () => {
        //Used to pass data from here to sportRoutes.jsx
        getZoomLevel(mMap.getZoom());
        setQueryParam("zoom", JSON.stringify(mMap.getZoom()));

        var mapCenter = calculateCenter(mMap.getBounds());
        setQueryParam("lat", mapCenter["lat"]);
        setQueryParam("lng", mapCenter["lng"]);
    };

    const changeBounds = () => {
        // Existing routes stay on the map until the new ones arrive, like the detail map.
        const newBounds = mMap.getBounds();

        var minLat = newBounds._southWest.lat,
            minLon = newBounds._southWest.lng,
            maxLat = newBounds._northEast.lat,
            maxLon = newBounds._northEast.lng;

        searchBody.params[0].bounds.min.lon = minLon;
        searchBody.params[0].bounds.min.lat = minLat;

        searchBody.params[0].bounds.max.lon = maxLon;
        searchBody.params[0].bounds.max.lat = maxLat;
    };

    const fetchRouteDetails = async (allRouteids) => {
        const batchSize = 100;
        const batches = [];

        // Split route IDs into batches of 100
        for (let i = 0; i < allRouteids.length; i += batchSize) {
            batches.push(allRouteids.slice(i, i + batchSize));
        }

        // Fetch all batches in parallel
        const batchPromises = batches.map(batch => {
            const body = {
                method: "getFull",
                params: [batch, "nl"],
            };
            return axios.post(DefaultLink, body);
        });

        const responses = await Promise.all(batchPromises);

        // Merge all batch results into a single data object
        const mergedData = {};
        responses.forEach(response => {
            Object.assign(mergedData, response.data);
        });

        return { data: mergedData };
    };

    const fetchRoutes = async () => {
        debouncedFetchRoutes.cancel();
        const requestVersion = requestVersionRef.current + 1;
        requestVersionRef.current = requestVersion;
        syncViewport();
        changeBounds();
        var groupId = [];
        const difficultyFilter = normalizeDifficultyFilterRange(filters["difficultyFilter"]);
        setCombinedRoutesData({
            requestVersion,
            routes: [],
        });

        if (filters["lengthFilter"]) {
            searchBody.params[0]["length.min"] = filters["lengthFilter"].min * 1000;
            searchBody.params[0]["length.max"] = filters["lengthFilter"].max * 1000;
        }

        if (filters["kindFilter"]) {
            searchBody.params[0]["type.id"] = filters["kindFilter"];
        }

        if (filters["categoryFilter"]) {
            //Alle looproutes
            if (filters["categoryFilter"] === 12045) {
                groupId.push(filters["categoryFilter"]);
                searchBody.params[0]["group.id"] = groupId;
                searchBody.params[0]["type.id"] = 9;
            }
            //All offroad bike routes: no type filter, so both mountainbike and gravel routes are returned
            else if (filters["categoryFilter"] === 1933) {
                groupId.push(filters["categoryFilter"]);
                searchBody.params[0]["group.id"] = groupId;
            }
            //Ruiter knooppunt
            else if (filters["categoryFilter"] === 215841) {
                searchBody.params[0]["owner.id"] === 215841;
            }
            //Specifieke filters
            else {
                groupId.push(filters["categoryFilter"]);
                searchBody.params[0]["group.id"] = groupId;
            }
        }

        if (filters["pavedFilter"]) {
            groupId.push(filters["pavedFilter"]);

            //paardrijden is een uitzondering omdat die geen category filter heeft.
            if (filters["group.id"] === "16185") {
                groupId.push(filters["group.id"]);
            }

            searchBody.params[0]["group.id"] = groupId;
            searchBody.params[0]["group.match"] = "all";
        }

        delete searchBody.params[0]["difficultyAuthor.min"];
        delete searchBody.params[0]["difficultyAuthor.max"];

        if (difficultyFilter && (difficultyFilter.min !== 0 || difficultyFilter.max !== 1)) {
            searchBody.params[0]["difficultyAuthor.min"] = difficultyFilter.min;
            searchBody.params[0]["difficultyAuthor.max"] = difficultyFilter.max;
        }

        if (!isComboRoute) {
            searchBody.params[0]['exclude.group.id'] = '55551';
        } else {
            searchBody.params[0]['exclude.group.id'] = null;
        }

        //Volledig leeg of geen group id ==> alles ophalen
        if (Object.keys(filters).length === 0 || filters['group.id'].length === 0) {

            onRequestStart && onRequestStart();

            let allRoutes = []
            //Alle mountainbike routes over vlaanderen
            searchBody.params[0]['owner.id'] = [30581]
            searchBody.params[0]['group.id'] = [1933]

            try {
                //Fetch all mountainbike routes
                const mtbResponse = await axios.post(DefaultLink, searchBody);
                allRoutes.push(mtbResponse.data);

                //Fetch de rest van de sporttypes
                searchBody.params[0]['owner.id'] = [];
                searchBody.params[0]['group.id'] = [16185, 26841, 12705, 12045, 42540];

                const restResponse = await axios.post(DefaultLink, searchBody);
                allRoutes.push(restResponse.data);
                if (requestVersion === requestVersionRef.current) {
                    setCombinedRoutesData({
                        requestVersion,
                        routes: allRoutes,
                    });
                }
            } catch (error) {
                console.error("Error fetching routes:", error);
                if (requestVersion === requestVersionRef.current) {
                    onRequestError && onRequestError();
                }
            } finally {
                onRequestDone && onRequestDone();
            }
        }
        //Filtered op group id
        else {
            onRequestStart && onRequestStart();
            if (filters["categoryFilter"] == 215841) {
                searchBody.params[0]['group.id'] = [];
                searchBody.params[0]['owner.id'] = 215841;
            } else if (filters['group.id'] !== 1933 && filters['categoryFilter'] !== 1933) {
                searchBody.params[0]['owner.id'] = []
            } else {
                searchBody.params[0]['owner.id'] = [30581]
            }

            if (!filters['categoryFilter'] && !filters['pavedFilter'] && filters['kindFilter'] != 54950) {
                searchBody.params[0]['group.id'] = filters['group.id'];
            }

            try {
                const response = await axios.post(DefaultLink, searchBody);
                if (requestVersion === requestVersionRef.current) {
                    setRoutesData(response.data);
                }
            } catch (error) {
                console.error("Error fetching filtered routes:", error);
                if (requestVersion === requestVersionRef.current) {
                    onRequestError && onRequestError();
                }
            } finally {
                // Filtered request done
                onRequestDone && onRequestDone();
            }
        }
    };

    useMapEvents({
        moveend: () => {
            syncViewport();
            debouncedFetchRoutes(fetchRoutes);
        },
    });

    const isRouteInMunicipality = (routeDetails, municipality) => {
        if (!routeDetails || !municipality) {
            return true;
        }

      if(routeDetails.breadcrumbRegions && routeDetails.breadcrumbRegions.length > 0) {
        var breadcrumbMunicipalityObj = routeDetails.breadcrumbRegions[0];
        var breadcrumbMunicipality = breadcrumbMunicipalityObj?.name?.nl;

        // case-insensitief vergelijken
        if(breadcrumbMunicipality?.toLowerCase().includes(municipality.name.toLowerCase())) {
          return true;
        }
      }

      if (routeDetails.beginAddress) {
        const match = routeDetails.beginAddress.match(POSTAL_CODE_REGEX);
        if (match && match[1] && match[1] === municipality.postalCode) {
                return true;
            }
        }

        if (routeDetails.pois && Array.isArray(routeDetails.pois)) {
            for (const poi of routeDetails.pois) {
                const address = poi?.location?.address;
                if (address && address.postalCode === municipality.postalCode) {
                    return true;
                }
            }
        }

        return false;
    };

    const isRouteInProvince = (routeDetails, ranges) => {
        if (!routeDetails || !ranges) return true;

        const rangeList = Array.isArray(ranges[0]) ? ranges : [ranges];

        const postcodeFrom = str => {
            const m = str && str.match(/\b(\d{4})\b/);
            return m ? parseInt(m[1], 10) : null;
        };

        const check = pc => rangeList.some(([min, max]) => pc >= min && pc <= max);

        const pcBegin = postcodeFrom(routeDetails.beginAddress);
        if (pcBegin && check(pcBegin)) return true;

        if (routeDetails.pois) {
            for (const poi of routeDetails.pois) {
                const pcPoi = postcodeFrom(poi?.location?.address?.postalCode);
                if (pcPoi && check(pcPoi)) return true;
            }
        }
        return false;
    };


    return null;
};

export default MapEvents;
